/*
 * pincela-animations.js
 * Animaciones a medida para pincela.cl:
 *   1) Parallax sutil del hero al mover el mouse (solo desktop, sin touch).
 *   2) Scroll-reveal en cascada de cada sección al entrar al viewport.
 *   3) Hover tilt + lift en tarjetas (.group dentro de .shopify-section).
 *   4) Click "paint splash" en botones primarios.
 *   5) Smooth scroll para anchors internos del home.
 *   6) Glow ambient animado en el fondo del hero.
 * Respeta prefers-reduced-motion y se desactiva en el editor de Shopify.
 */
(() => {
  if (window.__pincelaAnimationsLoaded) return;
  window.__pincelaAnimationsLoaded = true;

  const inEditor = !!(window.Shopify && window.Shopify.designMode);
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const noHover = window.matchMedia('(hover: none)').matches;

  // ---------- Estilos inyectados ----------
  const css = `
    .pp-reveal { opacity: 0; transform: translateY(28px); transition: opacity .8s cubic-bezier(.16,1,.3,1), transform .8s cubic-bezier(.16,1,.3,1); will-change: opacity, transform; }
    .pp-reveal.is-visible { opacity: 1; transform: none; }
    .pp-reveal[data-pp-delay="1"].is-visible { transition-delay: .08s; }
    .pp-reveal[data-pp-delay="2"].is-visible { transition-delay: .16s; }
    .pp-reveal[data-pp-delay="3"].is-visible { transition-delay: .24s; }
    .pp-reveal[data-pp-delay="4"].is-visible { transition-delay: .32s; }
    .pp-reveal[data-pp-delay="5"].is-visible { transition-delay: .40s; }

    .pp-hero-parallax { transition: transform .25s cubic-bezier(.16,1,.3,1); will-change: transform; }

    /* Tarjetas con tilt suave */
    .pp-tilt { transition: transform .35s cubic-bezier(.16,1,.3,1), box-shadow .35s cubic-bezier(.16,1,.3,1); transform-style: preserve-3d; will-change: transform; }
    .pp-tilt:hover { box-shadow: 0 24px 40px -16px rgba(15,15,15,0.18); }

    /* Glow animado detrás del hero */
    .pp-hero-glow { position: absolute; inset: 0; pointer-events: none; overflow: hidden; z-index: 0; }
    .pp-hero-glow::before, .pp-hero-glow::after {
      content: ''; position: absolute; width: 60vw; height: 60vw; border-radius: 50%;
      filter: blur(80px); opacity: .35; animation: pp-drift 18s ease-in-out infinite;
    }
    .pp-hero-glow::before { background: radial-gradient(circle, #FF5470 0%, transparent 70%); top: -20%; left: -10%; }
    .pp-hero-glow::after  { background: radial-gradient(circle, #E8FC5F 0%, transparent 70%); bottom: -20%; right: -10%; animation-delay: -9s; }
    @keyframes pp-drift {
      0%,100% { transform: translate(0,0) scale(1); }
      33%     { transform: translate(8vw, -4vw) scale(1.1); }
      66%     { transform: translate(-6vw, 5vw) scale(0.95); }
    }

    /* Splash al click en CTA primario */
    .pp-splash-host { position: relative; overflow: hidden; }
    .pp-splash {
      position: absolute; border-radius: 50%; pointer-events: none;
      background: radial-gradient(circle, rgba(232,252,95,.9) 0%, rgba(255,84,112,.6) 50%, transparent 70%);
      transform: translate(-50%,-50%) scale(0); opacity: .9;
      animation: pp-splash-out .6s cubic-bezier(.22,1,.36,1) forwards;
    }
    @keyframes pp-splash-out {
      0%   { transform: translate(-50%,-50%) scale(0);   opacity: .9; }
      100% { transform: translate(-50%,-50%) scale(1);   opacity: 0; }
    }

    /* Sparkles emoji-like en el hero kicker */
    .pp-pulse { animation: pp-pulse 2.4s ease-in-out infinite; }
    @keyframes pp-pulse {
      0%,100% { transform: scale(1);    filter: brightness(1); }
      50%     { transform: scale(1.05); filter: brightness(1.08); }
    }

    @media (prefers-reduced-motion: reduce) {
      .pp-reveal, .pp-reveal.is-visible { opacity: 1 !important; transform: none !important; transition: none !important; }
      .pp-hero-parallax, .pp-tilt { transform: none !important; transition: none !important; }
      .pp-hero-glow::before, .pp-hero-glow::after { animation: none !important; }
      .pp-pulse { animation: none !important; }
    }
  `;
  const style = document.createElement('style');
  style.setAttribute('data-pp', 'animations');
  style.textContent = css;
  document.head.appendChild(style);

  // ---------- 1) Scroll-reveal de secciones ----------
  function setupReveals() {
    const sections = Array.from(document.querySelectorAll('main .shopify-section'));
    if (!sections.length) return;
    sections.forEach((section, i) => {
      if (i === 0) return; // hero shows immediately
      const children = section.querySelectorAll(':scope > .section > .group, :scope > .section .group, :scope > section .group');
      if (children.length >= 2) {
        children.forEach((el, idx) => {
          el.classList.add('pp-reveal');
          el.setAttribute('data-pp-delay', String(Math.min(idx + 1, 5)));
        });
      } else {
        section.classList.add('pp-reveal');
      }
    });

    const targets = document.querySelectorAll('.pp-reveal');
    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });
    targets.forEach((el) => io.observe(el));
  }

  // ---------- 2) Hero parallax + ambient glow ----------
  function setupHeroParallax() {
    const heroMedia = document.querySelector('.hero__media-wrapper--desktop .hero__media');
    const heroSection = heroMedia && heroMedia.closest('.shopify-section');
    if (!heroMedia || !heroSection) return;

    // Glow ambient detrás
    if (!heroSection.querySelector('.pp-hero-glow')) {
      const glow = document.createElement('div');
      glow.className = 'pp-hero-glow';
      const cs = getComputedStyle(heroSection);
      if (cs.position === 'static') heroSection.style.position = 'relative';
      heroSection.prepend(glow);
    }

    if (noHover) return; // parallax solo en desktop

    heroMedia.classList.add('pp-hero-parallax');

    let rect = heroSection.getBoundingClientRect();
    const updateRect = () => { rect = heroSection.getBoundingClientRect(); };
    window.addEventListener('resize', updateRect, { passive: true });
    window.addEventListener('scroll', updateRect, { passive: true });

    let frame = 0;
    let targetX = 0, targetY = 0, currentX = 0, currentY = 0;
    const MAX = 14;

    function loop() {
      currentX += (targetX - currentX) * 0.08;
      currentY += (targetY - currentY) * 0.08;
      heroMedia.style.transform = `translate3d(${currentX.toFixed(2)}px, ${currentY.toFixed(2)}px, 0) scale(1.04)`;
      if (Math.abs(targetX - currentX) > 0.1 || Math.abs(targetY - currentY) > 0.1) {
        frame = requestAnimationFrame(loop);
      } else { frame = 0; }
    }

    heroSection.addEventListener('mousemove', (e) => {
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      targetX = ((e.clientX - cx) / rect.width) * MAX * -1;
      targetY = ((e.clientY - cy) / rect.height) * MAX * -1;
      if (!frame) frame = requestAnimationFrame(loop);
    }, { passive: true });

    heroSection.addEventListener('mouseleave', () => {
      targetX = 0; targetY = 0;
      if (!frame) frame = requestAnimationFrame(loop);
    });
  }

  // ---------- 3) Tilt suave en tarjetas .group ----------
  function setupCardTilt() {
    if (noHover) return;
    const cards = document.querySelectorAll('main .shopify-section .group');
    cards.forEach((card) => {
      // Solo aplicar a tarjetas con padding visible (no a wrappers)
      const cs = getComputedStyle(card);
      const radius = parseFloat(cs.borderRadius) || 0;
      if (radius < 8) return;

      card.classList.add('pp-tilt');
      let frame = 0;
      let targetRX = 0, targetRY = 0, targetT = 0;
      let curRX = 0, curRY = 0, curT = 0;

      function apply() {
        curRX += (targetRX - curRX) * 0.15;
        curRY += (targetRY - curRY) * 0.15;
        curT  += (targetT  - curT)  * 0.15;
        card.style.transform = `perspective(900px) rotateX(${curRX.toFixed(2)}deg) rotateY(${curRY.toFixed(2)}deg) translateY(${curT.toFixed(2)}px)`;
        if (
          Math.abs(targetRX - curRX) > 0.05 ||
          Math.abs(targetRY - curRY) > 0.05 ||
          Math.abs(targetT - curT) > 0.05
        ) {
          frame = requestAnimationFrame(apply);
        } else { frame = 0; }
      }

      card.addEventListener('mousemove', (e) => {
        const r = card.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width;  // 0..1
        const py = (e.clientY - r.top)  / r.height; // 0..1
        targetRY = (px - 0.5) *  6;  // ±3deg
        targetRX = (py - 0.5) * -6;  // ±3deg
        targetT  = -4;
        if (!frame) frame = requestAnimationFrame(apply);
      }, { passive: true });

      card.addEventListener('mouseleave', () => {
        targetRX = 0; targetRY = 0; targetT = 0;
        if (!frame) frame = requestAnimationFrame(apply);
      });
    });
  }

  // ---------- 4) Click splash en botones primarios ----------
  function setupSplash() {
    const buttons = document.querySelectorAll('main .shopify-section .button--primary, main .shopify-section a.button.primary, main .shopify-section .button.primary');
    buttons.forEach((btn) => {
      btn.classList.add('pp-splash-host');
      btn.addEventListener('click', (e) => {
        const r = btn.getBoundingClientRect();
        const size = Math.max(r.width, r.height) * 2.2;
        const x = (e.clientX || (r.left + r.width / 2)) - r.left;
        const y = (e.clientY || (r.top + r.height / 2)) - r.top;
        const splash = document.createElement('span');
        splash.className = 'pp-splash';
        splash.style.left = `${x}px`;
        splash.style.top = `${y}px`;
        splash.style.width = `${size}px`;
        splash.style.height = `${size}px`;
        btn.appendChild(splash);
        setTimeout(() => splash.remove(), 700);
      });
    });
  }

  // ---------- 5) Smooth scroll para anchors internos ----------
  function setupSmoothScroll() {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href === '#' || href.length < 2) return;
      const target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      const top = target.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  }

  // ---------- 6) Pulse en kicker del hero ----------
  function setupPulse() {
    const kicker = document.querySelector('main .shopify-section:first-of-type .hero p, main .shopify-section:first-of-type [data-block-type="text"]:first-of-type p');
    if (kicker && /✨|★|gratis/i.test(kicker.textContent || '')) {
      kicker.classList.add('pp-pulse');
    }
  }

  function init() {
    if (!reduceMotion) setupReveals();
    if (!reduceMotion && !inEditor) {
      setupHeroParallax();
      setupCardTilt();
      setupPulse();
    }
    setupSplash();
    setupSmoothScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
